import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetPricingPlanCap } from 'src/features/pricingPlan/api/caps/useGetPricingPlanCap';
import CapForm from 'src/features/pricingPlan/components/form/caps/CapForm';
import { pricingPlanCapFormSchema, PricingPlanCapFormSchemaValues } from 'src/features/pricingPlan/utils/formSchema';
import { PricingPlanCap } from 'src/features/pricingPlan/utils/types';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { useParking } from 'src/providers/ParkingProvider';

interface CapFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedCap?: PricingPlanCap;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: PricingPlanCapFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function CapFormModal<TData>({
    selectedCap,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: CapFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { planId = '' } = useParams();
    const { parkingId } = useParking();

    const { data: capData } = useGetPricingPlanCap({
        planId,
        parkingId,
        capId: selectedCap?.id ?? '',
        config: { enabled: !!planId && !!selectedCap?.id && isModalOpen },
    });

    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            cap_type: capData?.cap_type ?? undefined,
            cap_minutes: capData?.cap_minutes ?? undefined,
            rate_with_tax: capData?.rate_with_tax ?? undefined,
        }
    }, [capData]);

    const form = useForm<PricingPlanCapFormSchemaValues>({
        resolver: zodResolver(pricingPlanCapFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [capData, isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: PricingPlanCapFormSchemaValues) =>
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            ),
        [mutate, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={className}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <CapForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedCap && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CapFormModal;
